import { isEqual } from 'lodash';
import { diffMarkdown } from '../diff/diffMarkdown';
import { assertUnreachable } from '../enum';
import {
  SkillChangeApprovalOutcome,
  SkillChangeGlobalPromptValueChange,
  SkillChangeParameter,
  SkillChangeTargetKind,
  SkillChangeTestRun,
  SkillChangeTestRunStatus,
} from '../escalations/skillChangeApproval';
import {
  CardBlock,
  CardResolution,
  InteractiveCard,
  InteractiveCardType,
} from './types';

export const APPLY_SKILL_CHANGE_VERB = 'apply_skill_change';
export const DENY_SKILL_CHANGE_VERB = 'deny_skill_change';
export const RUN_SKILL_CHANGE_TESTS_VERB = 'run_skill_change_tests';

const DIFF_LABEL = 'View proposed changes';

export type BuildSkillChangeCardParams = {
  exceptionId: string;
  skillName: string;
  organizationName: string;
  targetKind: SkillChangeTargetKind;
  baseInstructions: string;
  proposedInstructions: string;
  baseParameters?: SkillChangeParameter[];
  proposedParameters?: SkillChangeParameter[];
  globalPromptValueChanges?: SkillChangeGlobalPromptValueChange[];
  rationale?: string;
  resolution?: CardResolution;
  testRun?: SkillChangeTestRun;
  fallbackUrl?: string;
};

export function buildSkillChangeCard(
  params: BuildSkillChangeCardParams
): InteractiveCard {
  const {
    exceptionId,
    skillName,
    organizationName,
    targetKind,
    baseInstructions,
    proposedInstructions,
    baseParameters,
    proposedParameters,
    globalPromptValueChanges,
    rationale,
    resolution,
    testRun,
    fallbackUrl = '',
  } = params;

  const { hunks, unifiedText } = diffMarkdown(
    baseInstructions,
    proposedInstructions
  );
  const isGlobalPrompt = targetKind === SkillChangeTargetKind.GLOBAL_PROMPT;
  const testsRunning = testRun?.status === SkillChangeTestRunStatus.RUNNING;
  const parameterDiffBlocks = proposedParameters
    ? buildParameterValueDiffBlocks(baseParameters ?? [], proposedParameters)
    : [];
  const parameterMetadataSummary = proposedParameters
    ? summarizeParameterMetadataChanges(
        baseParameters ?? [],
        proposedParameters
      )
    : undefined;
  const valueDiffBlocks = buildGlobalPromptValueDiffBlocks(
    globalPromptValueChanges ?? []
  );
  const showInstructionsDiff =
    baseInstructions !== proposedInstructions ||
    (parameterDiffBlocks.length === 0 &&
      valueDiffBlocks.length === 0 &&
      !parameterMetadataSummary);

  return {
    cardType: InteractiveCardType.SKILL_CHANGE_APPROVAL,
    targetKind,
    title: `Proposed skill update: ${skillName}`,
    blocks: [
      ...(rationale ? [{ type: 'text' as const, text: rationale }] : []),
      {
        type: 'facts' as const,
        facts: [
          { label: 'Skill', value: skillName },
          ...(!isGlobalPrompt
            ? [{ label: 'Organization', value: organizationName }]
            : []),
          ...(testRun && !testsRunning
            ? [{ label: 'Tests', value: describeSkillChangeTestRun(testRun) }]
            : []),
        ],
      },
      ...(showInstructionsDiff
        ? [
            {
              type: 'diff' as const,
              label: DIFF_LABEL,
              text: unifiedText || 'No differences detected.',
              hunks,
            },
          ]
        : []),
      ...parameterDiffBlocks,
      ...(parameterMetadataSummary
        ? [{ type: 'text' as const, text: parameterMetadataSummary }]
        : []),
      ...valueDiffBlocks,
    ],
    actions: [
      {
        verb: RUN_SKILL_CHANGE_TESTS_VERB,
        label: testsRunning ? 'Running tests…' : 'Run tests',
        style: 'default',
        data: { exceptionId },
        fallbackUrl,
        ...(testsRunning ? { isInProgress: true } : {}),
      },
      {
        verb: APPLY_SKILL_CHANGE_VERB,
        label: 'Apply',
        style: 'primary',
        data: { exceptionId, outcome: SkillChangeApprovalOutcome.APPLIED },
        fallbackUrl,
      },
      {
        verb: DENY_SKILL_CHANGE_VERB,
        label: 'Deny',
        style: 'danger',
        data: { exceptionId, outcome: SkillChangeApprovalOutcome.DENIED },
        fallbackUrl,
      },
    ],
    fallbackText: isGlobalPrompt
      ? `Proposed skill update for ${skillName}. Open Vooma to review and apply or deny.`
      : `Proposed skill update for ${skillName} in ${organizationName}. Open Vooma to review and apply or deny.`,
    resolution,
  };
}

export function displaySkillChangeOutcome(
  outcome: SkillChangeApprovalOutcome
): string {
  return outcome === SkillChangeApprovalOutcome.APPLIED ? 'Applied' : 'Denied';
}

export function describeSkillChangeTestRun(
  testRun: SkillChangeTestRun
): string {
  const ranCount = testRun.ranCount ?? 0;
  const failedCount = testRun.failedCount ?? 0;
  switch (testRun.status) {
    case SkillChangeTestRunStatus.RUNNING:
      return 'Running on the proposed change…';
    case SkillChangeTestRunStatus.PASSED:
      return `All ${ranCount} passed`;
    case SkillChangeTestRunStatus.FAILED:
      return `${failedCount} of ${ranCount} failed`;
    case SkillChangeTestRunStatus.NO_TESTS:
      return 'No tests are configured for this skill';
    case SkillChangeTestRunStatus.ERROR:
      return 'Could not run the tests';
    default:
      return assertUnreachable(testRun.status);
  }
}

function buildParameterValueDiffBlocks(
  baseParameters: SkillChangeParameter[],
  proposedParameters: SkillChangeParameter[]
): CardBlock[] {
  const baseValues = new Map(
    baseParameters.map((parameter) => [parameter.name, parameter.default])
  );
  return sortByName(proposedParameters).flatMap((parameter) => {
    const baseValue = baseValues.get(parameter.name);
    if (baseValue === parameter.default) return [];
    return [
      buildValueDiffBlock(
        baseValue === undefined
          ? `View new parameter ${parameter.name}`
          : `View proposed changes to the default value of parameter ${parameter.name}`,
        baseValue ?? '',
        parameter.default
      ),
    ];
  });
}

function summarizeParameterMetadataChanges(
  baseParameters: SkillChangeParameter[],
  proposedParameters: SkillChangeParameter[]
): string | undefined {
  const proposedByName = new Map(
    proposedParameters.map((parameter) => [parameter.name, parameter])
  );
  const changes = sortByName(baseParameters).flatMap((base) => {
    const proposed = proposedByName.get(base.name);
    if (!proposed) return [`removes ${base.name}`];
    return [
      ...((base.description ?? '') !== (proposed.description ?? '')
        ? [`edits ${base.name} description`]
        : []),
      ...(!isEqual(base.defaultTools ?? [], proposed.defaultTools ?? [])
        ? [`edits ${base.name} tools`]
        : []),
    ];
  });
  return changes.length > 0 ? `Also ${changes.join(', ')}.` : undefined;
}

function buildGlobalPromptValueDiffBlocks(
  changes: SkillChangeGlobalPromptValueChange[]
): CardBlock[] {
  return sortByName(changes).map((change) =>
    buildValueDiffBlock(
      `View proposed changes to parameter ${change.name} for this SOP`,
      change.baseValue ?? change.defaultValue,
      change.proposedValue
    )
  );
}

function buildValueDiffBlock(
  label: string,
  baseValue: string,
  proposedValue: string
): CardBlock {
  const { hunks, unifiedText } = diffMarkdown(baseValue, proposedValue);
  return { type: 'diff', label, text: unifiedText, hunks };
}

function sortByName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}
